// 9.18.10.4 · D-830 — reading the layout of a person's own Instagram download.
//
// Meta's layout has changed over the years and is not documented, so nothing
// here depends on one key name. Observed forms, all handled the same way:
//   - your_instagram_activity/media/posts_1.json: [{ media: [{ uri, creation_timestamp, title }] }]
//     A single-photo post keeps its time inside media[]; a carousel also has one
//     at the top, which its items inherit when they lack their own.
//   - reels / stories / IGTV / archived / profile photos: objects or lists of
//     objects with { uri, creation_timestamp } under some wrapper key.
//   - Older downloads: media.json with { photos: [{ path, taken_at }], videos: [...] }.
// The walker collects every (file path, time) pair it finds in the person's own
// activity files. Conversation files are never read.

export type ArchiveCategory = 'posts' | 'reels' | 'stories' | 'igtv' | 'archived' | 'profile' | 'other';

export type MediaFacts = { takenAt: Date | null; category: ArchiveCategory | null };

/**
 * The folder everything sits under, if the ZIP wraps the download in one
 * (e.g. "instagram-name-2026-10-15-abc/"), with its trailing slash; else "".
 */
export function archiveRoot(names: string[]): string {
  const known = /^(media|your_instagram_activity|content|personal_information|connections|logged_information|preferences|security_and_login_information|ads_information|apps_and_websites_off_of_instagram|messages)\//;
  if (names.some((name) => known.test(name))) return '';
  const files = names.filter((name) => !name.endsWith('/'));
  if (!files.length) return '';
  const first = files[0].split('/')[0];
  if (!first || files.some((name) => !name.startsWith(`${first}/`))) return '';
  return names.some((name) => known.test(name.slice(first.length + 1))) ? `${first}/` : '';
}

/** Which of the person's own JSON files can describe their media. Never conversations. */
export function isMetadataFile(path: string): boolean {
  if (!/\.json$/i.test(path)) return false;
  if (/(^|\/)(messages|inbox|message_requests)(\/|$)/i.test(path)) return false;
  const file = path.split('/').pop() ?? '';
  return /^(posts(_\d+)?|reels|stories|igtv(_videos)?|archived_posts|profile_photos|media|other_content|content)\.json$/i.test(file);
}

export function categoryFromPath(path: string): ArchiveCategory | null {
  const p = path.toLowerCase();
  if (/reel/.test(p)) return 'reels';
  if (/stor(y|ies)/.test(p)) return 'stories';
  if (/igtv/.test(p)) return 'igtv';
  if (/archived/.test(p)) return 'archived';
  if (/profile/.test(p)) return 'profile';
  if (/posts?/.test(p)) return 'posts';
  return null;
}

// Instagram began in October 2010; a time outside its years is not one of its posts.
function validTime(date: Date, now: Date): Date | null {
  const t = date.getTime();
  return Number.isFinite(t) && t >= Date.UTC(2010, 9, 6) && t <= now.getTime() + 24 * 3600 * 1000 ? date : null;
}

function timeOf(value: Record<string, unknown>, now: Date): Date | null {
  if (typeof value.creation_timestamp === 'number') return validTime(new Date(value.creation_timestamp * 1000), now);
  if (typeof value.taken_at === 'string') return validTime(new Date(value.taken_at), now);
  if (typeof value.taken_at === 'number') return validTime(new Date(value.taken_at * 1000), now);
  return null;
}

/** Strips "./" and leading slashes; Meta's paths are relative to the download's root. */
export function normalizeUri(uri: string): string {
  return uri.replace(/^\.\//, '').replace(/^\/+/, '');
}

/**
 * Every (path → facts) pair in one parsed JSON file. A carousel's items
 * inherit the post's time when they have none of their own.
 */
export function collectMediaFacts(json: unknown, fileCategory: ArchiveCategory | null, now: Date = new Date()): Map<string, MediaFacts> {
  const facts = new Map<string, MediaFacts>();
  const visit = (value: unknown, inherited: Date | null, keyCategory: ArchiveCategory | null, depth: number) => {
    if (depth > 12 || value === null || typeof value !== 'object') return;
    if (Array.isArray(value)) { for (const item of value) visit(item, inherited, keyCategory, depth + 1); return; }
    const record = value as Record<string, unknown>;
    const own = timeOf(record, now) ?? inherited;
    const path = typeof record.uri === 'string' ? record.uri : typeof record.path === 'string' ? record.path : null;
    if (path) {
      const key = normalizeUri(path);
      const before = facts.get(key);
      // The earliest time wins: the same file can appear in posts and archived.
      const takenAt = before?.takenAt && own ? (before.takenAt < own ? before.takenAt : own) : before?.takenAt ?? own;
      facts.set(key, { takenAt, category: before?.category ?? keyCategory ?? fileCategory });
    }
    for (const [key, child] of Object.entries(record)) {
      if (child && typeof child === 'object') visit(child, own, categoryFromPath(key) ?? keyCategory, depth + 1);
    }
  };
  visit(json, null, null, 0);
  return facts;
}
